/**
 * Skjema — offentlige påmeldingsskjemaer (PEOPLE.md, fane 2, «Skjema»).
 *
 * ---------------------------------------------------------------------------------------------
 * TWO AUDIENCES, TWO SHAPES, AND THE SPLIT IS A SECURITY BOUNDARY.
 *
 * The ORGANISER's shape is `SignupFormDto` in `volunteers.ts`: it carries the settings, the
 * response count and — crucially — the `publicToken`'s effects but never the token itself in a
 * list somebody might screenshot. The VISITOR's shape is `PublicFormDto` below, and it is
 * deliberately smaller: a name, an intro, the questions, and nothing about who else answered.
 *
 * A visitor reaching this contract has no session at all. There is no organisation in their
 * request, no actor to attribute a write to, and nothing they send may decide which tenant they
 * are writing into — the TOKEN decides that, inside a SECURITY DEFINER function, which is why
 * `submitSignupRequest` has no `editionId` and no `organizationId`.
 */
import { z } from 'zod';

import { personSystemKeySchema } from './person-standard-fields.js';
import { signupFieldTypeSchema, signupFormStatusSchema } from './volunteers.js';
import { signupFormKindSchema, supplierSignupPartSchema } from './suppliers.js';

/** Fields a visitor sees. No `id` of anything they could use elsewhere beyond the field's own. */
export const publicFieldSchema = z
  .object({
    id: z.uuid(),
    key: z.string(),
    label: z.string(),
    help: z.string().nullable(),
    type: signupFieldTypeSchema,
    required: z.boolean(),
    /**
     * ALWAYS RESOLVED, never null — this is the visitor's shape, and «avdelingsvalg» and
     * «dagvalg» «henter fra systemet i stedet for å vedlikeholdes manuelt». The server reads the
     * year's departments and days when it draws the form, so a department added this morning is
     * on the form this morning.
     */
    options: z.array(z.object({ value: z.string(), label: z.string() })),
    /**
     * Standardfeltet på personen feltet ER (0920). Skjemaet tegner da den faste kontrollen
     * («Allergener og kosthold» som ett felt), og svaret havner på personen.
     */
    systemKey: personSystemKeySchema.nullable().default(null),
  })
  .meta({ id: 'PublicField' });
export type PublicFieldDto = z.infer<typeof publicFieldSchema>;

export const publicFormSchema = z
  .object({
    name: z.string(),
    intro: z.string().nullable(),
    locale: z.string(),
    /** What the year is called, so the page says what somebody is signing up for. */
    editionName: z.string(),
    organizationName: z.string(),
    fields: z.array(publicFieldSchema),
    /**
     * Whether it takes answers right now. A CLOSED form still renders — with its own message —
     * rather than 404ing: somebody following a poster deserves «dette skjemaet er stengt» rather
     * than a page that looks broken.
     */
    open: z.boolean(),
    /** Why it is closed, when it is: `closed`, `not_open_yet` or `full`. */
    closedReason: z.enum(['closed', 'not_open_yet', 'full']).nullable(),
    /** 1096: et skjema av typen organisasjon spør i tillegg om firmanavn og ansatte. */
    kind: signupFormKindSchema.default('volunteer'),
  })
  .meta({ id: 'PublicForm' });
export type PublicFormDto = z.infer<typeof publicFormSchema>;

/** How long an answer to one question may be. A paragraph, not an essay. */
export const MAX_ANSWER_TEXT = 2000;

export const submitSignupRequestSchema = z
  .object({
    /**
     * TO NAVNEFELT (QA13 #37, vedtak 253.15). Fornavn og etternavn lagres på personen, og
     * visningsnavnet settes sammen av dem. `name` alene tas fortsatt imot (eldre klienter).
     */
    givenName: z.string().trim().min(1).max(60).optional(),
    familyName: z.string().trim().min(1).max(60).optional(),
    name: z.string().trim().min(1).max(120).optional(),
    email: z.email().max(200),
    /**
     * TELEFON ER PÅKREVD (persontypene §3.4.5): byggeren sier «Alltid med, alltid påkrevd», og
     * skjemaet og API-et sier nå det samme.
     */
    phone: z.string().trim().min(1).max(40),
    /**
     * «Samtykke til lagring av opplysninger er et påkrevd felt.» A literal `true`: a boolean that
     * defaulted would be a consent nobody gave, and the timestamp is stamped by the server rather
     * than sent, because a client-supplied one is not evidence of anything.
     */
    consent: z.literal(true),
    /**
     * The answers, by FIELD ID. Keyed rather than positional so a form edited between the page
     * being drawn and the answer arriving cannot shift every answer one question along.
     */
    answers: z.record(z.uuid(), z.unknown()),
    /** 1096: firmaet og de ansatte, på et skjema av typen organisasjon. Kontaktpersonen er feltene over. */
    organization: supplierSignupPartSchema.optional(),
  })
  .refine((value) => value.name !== undefined || value.givenName !== undefined, {
    message: 'A name is required.',
    path: ['givenName'],
  })
  .meta({ id: 'SubmitSignupRequest' });

/** Visningsnavnet en påmelding gir: «Fornavn Etternavn», eller navnet slik det kom. */
export function signupDisplayName(body: {
  name?: string | undefined;
  givenName?: string | undefined;
  familyName?: string | undefined;
}): string {
  const joined = [body.givenName, body.familyName]
    .map((part) => part?.trim() ?? '')
    .filter((part) => part !== '')
    .join(' ');
  return joined !== '' ? joined : (body.name ?? '').trim();
}
export type SubmitSignupRequest = z.infer<typeof submitSignupRequestSchema>;

export const submitSignupResponseSchema = z
  .object({
    /** Nothing identifying comes back. A visitor gets told it worked, not who else is on the list. */
    received: z.literal(true),
  })
  .meta({ id: 'SubmitSignupResponse' });
export type SubmitSignupResponse = z.infer<typeof submitSignupResponseSchema>;

// -------------------------------------------------------------------- the organiser's writes

const formNameSchema = z.string().trim().min(1).max(120);

export const createSignupFormRequestSchema = z
  .object({
    editionId: z.uuid(),
    name: formNameSchema,
    intro: z.string().trim().max(2000).nullish(),
    /** 1096: frivillig (standard) eller organisasjon. Kan ikke endres etterpå. */
    kind: signupFormKindSchema.optional(),
  })
  .meta({ id: 'CreateSignupFormRequest' });
export type CreateSignupFormRequest = z.infer<typeof createSignupFormRequestSchema>;

export const updateSignupFormRequestSchema = z
  .object({
    name: formNameSchema.optional(),
    intro: z.string().trim().max(2000).nullish(),
    status: signupFormStatusSchema.optional(),
    opensAt: z.iso.datetime().nullish(),
    closesAt: z.iso.datetime().nullish(),
    maxResponses: z.number().int().min(1).max(100_000).nullish(),
    locale: z.enum(['nb', 'en']).optional(),
    /* «Krever innlogging» og «Send bekreftelse på e-post» er borte (1090): frivillige logger aldri
       inn, og bekreftelsen er en av de automatiske e-postene (`volunteer-messages`). */
    createsContact: z.boolean().optional(),
    notifyDepartmentId: z.uuid().nullish(),
    requiresApproval: z.boolean().optional(),
    /** 1096: fristen kontaktpersonen kan rette de ansatte fram til. */
    memberDeadline: z.iso.datetime().nullish(),
  })
  .refine((value) => Object.keys(value).length > 0, {
    message: 'At least one field must be given.',
  })
  .meta({ id: 'UpdateSignupFormRequest' });
export type UpdateSignupFormRequest = z.infer<typeof updateSignupFormRequestSchema>;

/** «Et stengt skjema fra i fjor skal kunne kopieres til årets utgave.» */
export const copySignupFormRequestSchema = z
  .object({
    /** Which year the copy lands in. Never the same year: that would be a duplicate, not a copy. */
    editionId: z.uuid(),
    name: formNameSchema.optional(),
  })
  .meta({ id: 'CopySignupFormRequest' });
export type CopySignupFormRequest = z.infer<typeof copySignupFormRequestSchema>;

const fieldLabelSchema = z.string().trim().min(1).max(200);

export const createSignupFieldRequestSchema = z
  .object({
    /**
     * PERSONFELTET dette skjemafeltet ER («Legg til felt» viser personfeltene først). Er det satt,
     * bestemmer personfeltet type og valg, og etiketten er personfeltets når den ikke er gitt.
     */
    personFieldId: z.uuid().optional(),
    label: fieldLabelSchema.optional(),
    type: signupFieldTypeSchema.optional(),
    help: z.string().trim().max(500).nullish(),
    required: z.boolean().optional(),
    options: z.array(z.string().trim().min(1).max(120)).max(60).nullish(),
  })
  .refine(
    (value) =>
      value.personFieldId !== undefined || (value.label !== undefined && value.type !== undefined),
    { message: 'A label and a type are required for a new question.', path: ['label'] },
  )
  .meta({ id: 'CreateSignupFieldRequest' });
export type CreateSignupFieldRequest = z.infer<typeof createSignupFieldRequestSchema>;

export const updateSignupFieldRequestSchema = z
  .object({
    label: fieldLabelSchema.optional(),
    help: z.string().trim().max(500).nullish(),
    required: z.boolean().optional(),
    options: z.array(z.string().trim().min(1).max(120)).max(60).nullish(),
  })
  .refine((value) => Object.keys(value).length > 0, {
    message: 'At least one field must be given.',
  })
  .meta({ id: 'UpdateSignupFieldRequest' });
export type UpdateSignupFieldRequest = z.infer<typeof updateSignupFieldRequestSchema>;

/**
 * ET SKJEMASVAR RETTET AV ARRANGØREN (QA13 #39). Revidert som enhver annen skriving, så «hvem
 * endret svaret, og hva sto der» kan leses i loggen. Formen følger feltets type, og API-et sjekker
 * den. Et felt som ER et personfelt rettes på personen, ikke her.
 */
export const updateSignupAnswerRequestSchema = z
  .object({ value: z.unknown() })
  .meta({ id: 'UpdateSignupAnswerRequest' });
export type UpdateSignupAnswerRequest = z.infer<typeof updateSignupAnswerRequestSchema>;

/**
 * «Felt kan flyttes i rekkefølge.» The WHOLE order, not a move-one-up call, for the reason the
 * shift assignee list is replaced whole: two people reordering at once must not depend on arrival
 * order, and a list of ids is a state the server can validate against what exists.
 */
export const reorderSignupFieldsRequestSchema = z
  .object({ fieldIds: z.array(z.uuid()).min(1).max(100) })
  .meta({ id: 'ReorderSignupFieldsRequest' });
export type ReorderSignupFieldsRequest = z.infer<typeof reorderSignupFieldsRequestSchema>;

/**
 * «OPPRETT KONTAKT FOR N SOM ALLEREDE ER GODKJENT?» (sak 206). Svaret på både spørsmålet (hvor
 * mange godkjente på skjemaet står utenfor kontaktlista) og handlingen (hvor mange som ble lagt
 * inn). Et tall, ikke navnene: dialogen spør om et antall, og lista finnes under Frivillige.
 */
export const signupFormContactBackfillSchema = z
  .object({ count: z.number().int().nonnegative() })
  .meta({ id: 'SignupFormContactBackfill' });
export type SignupFormContactBackfill = z.infer<typeof signupFormContactBackfillSchema>;
